import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { Shield, Users, Award, Clock, CheckCircle, ArrowRight, Target, Heart } from 'lucide-react';
import { stats } from '../data/mock';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: "Integrity",
      description: "We uphold the highest ethical standards in every aspect of our operations, ensuring trust and reliability."
    },
    {
      icon: Target,
      title: "Excellence",
      description: "We strive for excellence in service delivery, continuously improving our methods and training protocols."
    },
    {
      icon: Users,
      title: "Teamwork",
      description: "Our success is built on collaboration between our guards, clients, and management teams."
    },
    {
      icon: Heart,
      title: "Dedication",
      description: "We are dedicated to protecting what matters most to our clients - their people, property, and peace of mind."
    }
  ];

  const milestones = [
    { year: "1993", event: "Company founded by former law enforcement officers" },
    { year: "2000", event: "Expanded operations to 10 states" },
    { year: "2008", event: "Reached 100 office locations nationwide" },
    { year: "2015", event: "Launched 24/7 mobile patrol services" },
    { year: "2020", event: "Achieved 600+ office locations across all 50 states" },
    { year: "2024", event: "Serving over 3,400 satisfied clients nationwide" }
  ];

  return (
    <>
      <SEO 
        title="About Us"
        description="Learn about Armed Security Service - a trusted security company with over 30 years of experience providing professional security guard services nationwide. Licensed, bonded & insured."
        keywords="about armed security service, security company history, professional security guards, trusted security provider"
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              About <span className="text-orange-500">Armed Security Service</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Founded by former law enforcement officers, we've grown to become one of the most trusted 
              security companies in the United States, protecting businesses and families nationwide.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-400 leading-relaxed">
                <p>
                  Armed Security Service was established with a clear mission: to provide exceptional 
                  security services that protect people, property, and peace of mind. Our founders, 
                  with decades of combined experience in law enforcement and military service, 
                  recognized the need for professional, reliable security solutions.
                </p>
                <p>
                  Over the years, we've grown from a small regional operation to a nationwide 
                  security provider with over 600 offices across all 50 states. This growth reflects 
                  our commitment to excellence and the trust our clients place in us.
                </p>
                <p>
                  Today, we employ over 12,000 professional security guards who undergo rigorous 
                  training and background checks. Our team includes former military personnel, 
                  law enforcement officers, and security professionals with diverse expertise.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1485230405346-71acb9518d9c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGd1YXJkfGVufDB8fHx8MTc2ODg5MjgxN3ww&ixlib=rb-4.1.0&q=85"
                alt="Security Professional"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-orange-500 rounded-xl p-6 shadow-xl">
                <div className="text-4xl font-bold text-white">30+</div>
                <div className="text-white/80">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/80 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our Core <span className="text-orange-500">Values</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              These principles guide everything we do and define who we are as a company.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div key={value.title} className="bg-slate-800 rounded-xl p-6 text-center hover:bg-slate-750 transition-colors">
                <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-gray-400 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our <span className="text-orange-500">Journey</span>
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-orange-500/30"></div>
            {milestones.map((milestone, index) => (
              <div key={milestone.year} className={`relative flex items-center mb-8 ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}>
                <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8'}`}>
                  <div className="bg-slate-900 rounded-lg p-4">
                    <div className="text-orange-500 font-bold text-lg">{milestone.year}</div>
                    <div className="text-gray-300 text-sm">{milestone.event}</div>
                  </div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-orange-500 rounded-full border-4 border-slate-800"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
            Contact us today to learn how we can help protect your business, property, or event.
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all"
          >
            Get a Free Quote
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default AboutPage;